import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { ArrowUpRightIcon, QuoteIcon } from 'lucide-react';
const caseStudies = [{
  company: 'Florian Bretschneider',
  industry: 'Coaching / Appointment Setting',
  logo: 'FB',
  challenge: 'Das Team arbeitete mit Google Sheets bei 70-100.000€ monatlichem Cash Collect. Viele Leads gingen unter, Follow-up Prozesse waren chaotisch, Calls wurden vergessen.',
  solution: 'Komplette Close CRM Implementierung mit automatisierten Prozessen, Follow-up Sequenzen und übersichtlichem Pipeline-Management für das gesamte Sales Team.',
  results: [{
    metric: '510k€',
    label: 'Cash Collect/Monat'
  }, {
    metric: '800k€',
    label: 'Auftragsvolumen'
  }, {
    metric: '5x',
    label: 'Umsatzsteigerung'
  }],
  quote: 'Wir haben das CRM-System mit sämtlichen Prozessen und Automatisierungen. Stehen jetzt bei 510.000 Euro Cash Collect monatlich mit einem Auftragsvolumen von über 800.000 Euro. Das Team hat viel mehr Bock, weil es einfach viel übersichtlicher ist.',
  author: 'Florian Bretschneider',
  role: 'Geschäftsführer'
}, {
  company: 'Albert Pappelheim',
  industry: 'Vertriebsagentur für Coaches & Berater',
  logo: 'AP',
  challenge: 'Über 1,5 Jahre mit Excel-Listen gearbeitet. Unstrukturiert, zeitaufwendig, nur 100-150 Anwahlversuche pro Tag möglich. Keine automatischen Aufzeichnungen, schwierige Vertriebskontrolle.',
  solution: 'Close CRM mit automatisierten Workflows, Live-Call-Monitoring, automatischen Gesprächsaufzeichnungen und intelligenter Lead-Priorisierung für mehrere Kundenprojekte.',
  results: [{
    metric: '3-4x',
    label: 'Mehr Effizienz'
  }, {
    metric: '400',
    label: 'Anwahlversuche/Tag'
  }, {
    metric: '100%',
    label: 'Gesprächsaufzeichnung'
  }],
  quote: 'Wir arbeiten drei- bis viermal effizienter. An einem Tag schaffen wir bestimmt 300-400 Anwahlversuche statt 100-150. Ich kann jedes Gespräch sofort mithören, sie werden automatisch aufgezeichnet - das spart immens viel Zeit.',
  author: 'Albert Pappelheim',
  role: 'Geschäftsführer'
}, {
  company: 'Adrian Wettig',
  industry: 'E-Commerce / Dropshipping Mentoring',
  logo: 'AW',
  challenge: 'Bei 30.000€ Umsatz/Monat mit Google Sheets gearbeitet. Unübersichtlich, hoher Verwaltungsaufwand, Fehler durch doppelte oder vergessene Anrufe. Über 13.000 Leads waren kaum verwaltbar.',
  solution: 'Close CRM Setup mit klaren Prozessen, Filtern, Status-Tracking und Kommentarfunktionen. Schnelle Implementierung innerhalb einer Woche trotz komplexer Anforderungen.',
  results: [{
    metric: '70k€',
    label: 'Umsatz/Monat'
  }, {
    metric: '2x',
    label: 'Umsatzverdopplung'
  }, {
    metric: '13k+',
    label: 'Leads verwaltet'
  }],
  quote: 'Das CRM erleichtert uns das Leben extrem. Ich kann mich viel mehr um Marketing kümmern und muss mich nicht mehr mit Verwaltungszeug rumärgern. Wir konnten unseren Umsatz von 30.000€ auf 70.000€ verdoppeln.',
  author: 'Adrian Wettig',
  role: 'Geschäftsführer'
}];
export function CaseStudies() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section className="py-24 lg:py-32 bg-[#0c0c0c]" id="case-studies" ref={ref} data-id="element-25">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-26">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center max-w-3xl mx-auto mb-16" data-id="element-27">
          <span className="inline-block px-4 py-1.5 rounded-full bg-emerald-500/10 text-emerald-400 text-sm font-medium mb-6" data-id="element-28">
            Erfolgsgeschichten
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight" data-id="element-29">
            Echte Ergebnisse von echten Kunden
          </h2>
          <p className="mt-6 text-lg text-gray-400" data-id="element-30">
            Sehen Sie, wie andere Unternehmen mit unserer Close CRM
            Implementierung ihren Vertrieb transformiert haben.
          </p>
        </motion.div>

        <div className="space-y-8" data-id="element-31">
          {caseStudies.map((study, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: index * 0.15
        }} className="rounded-2xl bg-[#1a1a1a] border border-white/5 overflow-hidden" data-id="element-32">
              <div className="grid lg:grid-cols-3 gap-8 p-8" data-id="element-33">
                {/* Company info */}
                <div className="lg:border-r lg:border-white/5 lg:pr-8" data-id="element-34">
                  <div className="flex items-center gap-4 mb-6" data-id="element-35">
                    <div className="w-14 h-14 rounded-xl bg-[#0ea5e9]/10 flex items-center justify-center text-[#0ea5e9] font-bold text-xl" data-id="element-36">
                      {study.logo}
                    </div>
                    <div data-id="element-37">
                      <h3 className="text-xl font-bold text-white" data-id="element-38">
                        {study.company}
                      </h3>
                      <p className="text-gray-500 text-sm" data-id="element-39">{study.industry}</p>
                    </div>
                  </div>

                  <div className="space-y-4" data-id="element-40">
                    <div data-id="element-41">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-42">
                        Herausforderung
                      </h4>
                      <p className="text-gray-400 text-sm" data-id="element-43">{study.challenge}</p>
                    </div>
                    <div data-id="element-44">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-45">
                        Lösung
                      </h4>
                      <p className="text-gray-400 text-sm" data-id="element-46">{study.solution}</p>
                    </div>
                  </div>
                </div>

                {/* Results */}
                <div className="lg:border-r lg:border-white/5 lg:pr-8" data-id="element-47">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-6" data-id="element-48">
                    Ergebnisse
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-1 gap-4" data-id="element-49">
                    {study.results.map((result, i) => <div key={i} className="text-center p-4 rounded-xl bg-[#0a0a0a]" data-id="element-50">
                        <div className="text-2xl lg:text-3xl font-bold text-emerald-400 break-words" data-id="element-51">
                          {result.metric}
                        </div>
                        <div className="text-xs text-gray-500 mt-2 break-words" data-id="element-52">
                          {result.label}
                        </div>
                      </div>)}
                  </div>
                </div>

                {/* Quote */}
                <div className="flex flex-col justify-between" data-id="element-53">
                  <div data-id="element-54">
                    <QuoteIcon className="w-8 h-8 text-[#0ea5e9]/30 mb-4" data-id="element-55" />
                    <p className="text-gray-300 italic leading-relaxed" data-id="element-56">
                      "{study.quote}"
                    </p>
                  </div>
                  <div className="mt-6 flex items-center gap-3" data-id="element-57">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0ea5e9] to-[#06b6d4] flex items-center justify-center text-white font-semibold text-sm" data-id="element-58">
                      {study.author.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div data-id="element-59">
                      <p className="text-white font-medium text-sm" data-id="element-60">
                        {study.author}
                      </p>
                      <p className="text-gray-500 text-xs" data-id="element-61">{study.role}</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.6
      }} className="mt-12 text-center" data-id="element-62">
          <a href="#contact" className="group inline-flex items-center gap-2 text-[#0ea5e9] font-semibold hover:underline" data-id="element-63">
            Werden Sie unsere nächste Erfolgsgeschichte
            <ArrowUpRightIcon className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" data-id="element-64" />
          </a>
        </motion.div>
      </div>
    </section>;
}